export type ImageWindowingPolicy = {
  windowLevel: number;
  windowWidth: number;
  invert?: boolean;
};

export type ImageHuRange = {
  huMin?: number;
  huMax?: number;
};

export function toFiniteNumber(value: unknown): number | undefined {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function hasValidHuRange(range: ImageHuRange | undefined) {
  const huMin = toFiniteNumber(range?.huMin);
  const huMax = toFiniteNumber(range?.huMax);
  return huMin !== undefined && huMax !== undefined && huMax > huMin;
}

export function uint8ToApproximateHu(
  value: number,
  range: ImageHuRange | undefined,
) {
  const huMin = toFiniteNumber(range?.huMin);
  const huMax = toFiniteNumber(range?.huMax);
  if (huMin === undefined || huMax === undefined || huMax <= huMin) {
    return value;
  }
  return (value / 255) * (huMax - huMin) + huMin;
}

export function windowHuToByte(
  hu: number,
  policy: ImageWindowingPolicy | undefined,
) {
  if (!policy) {
    return Math.max(0, Math.min(255, Math.round(hu)));
  }
  const windowLevel = toFiniteNumber(policy.windowLevel) ?? 127.5;
  const windowWidth = Math.max(1, toFiniteNumber(policy.windowWidth) ?? 255);
  const normalized =
    windowWidth <= 1
      ? hu >= windowLevel
        ? 1
        : 0
      : (hu - (windowLevel - 0.5)) / (windowWidth - 1) + 0.5;
  const clamped = Math.max(0, Math.min(1, normalized));
  const byte = Math.round(clamped * 255);
  return policy.invert ? 255 - byte : byte;
}

export function uint8ValueToWindowedByte({
  value,
  huRange,
  policy,
}: {
  value: number;
  huRange?: ImageHuRange;
  policy?: ImageWindowingPolicy;
}) {
  if (!policy) {
    return value;
  }
  return windowHuToByte(uint8ToApproximateHu(value, huRange), policy);
}

export function calculateWindowLevelWidthFromDrag({
  current,
  deltaX,
  deltaY,
  scale = 2,
}: {
  current: ImageWindowingPolicy | undefined;
  deltaX: number;
  deltaY: number;
  scale?: number;
}): ImageWindowingPolicy | null {
  if (!current) return null;
  const windowLevel = toFiniteNumber(current.windowLevel);
  const windowWidth = toFiniteNumber(current.windowWidth);
  if (windowLevel === undefined || windowWidth === undefined) return null;

  const lower = windowLevel - windowWidth / 2 + (deltaY - deltaX) * scale;
  const upper = windowLevel + windowWidth / 2 + (deltaY + deltaX) * scale;
  if (lower > upper) return null;

  return {
    windowLevel: (upper + lower) * 0.5,
    windowWidth: upper - lower,
    invert: current.invert,
  };
}
